import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '../../hooks/useAuth';
import { useUIStore } from '../../stores/uiStore';
import { ThemeToggleCompact } from '../../components/common/theme-toggle';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from '../../components/ui/form';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import { AlertCircle, ArrowLeft, Loader2, Trash2, Star, Shield, Users } from 'lucide-react';
import { LoadingSpinner } from '../../components/ui/loading-spinner';
import { Checkbox } from '../../components/ui/checkbox';
import { Badge } from '../../components/ui/badge';
import { toast } from '../../components/ui/toast';
import { api, getRoles, updateOrgUser, deleteOrgUser } from '../../services/api';
import type { User, Role } from '../../types';

interface RoleOption {
  id: string;
  name: string;
  description: string;
  isSystem: boolean;
  permissions: string[];
}

const SYSTEM_ROLES: RoleOption[] = [
  {
    id: 'user',
    name: 'User',
    description: 'Basic access — can view their own profile and authenticate via applications',
    isSystem: true,
    permissions: [],
  },
  {
    id: 'admin',
    name: 'Organization Admin',
    description: 'Can manage applications and users within this organization',
    isSystem: true,
    permissions: [],
  },
];

const editUserSchema = z.object({
  name: z.string()
    .min(2, 'Name must be at least 2 characters')
    .max(100, 'Name must be 100 characters or less')
    .optional(),
  email: z.string().email('Invalid email address').optional(),
  selectedRoleIds: z.array(z.string()).min(1, 'Select at least one role'),
  primaryRoleId: z.string().min(1, 'Choose a primary role'),
  is_active: z.boolean().optional(),
  email_verified: z.boolean().optional(),
});

export function EditUser() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { isSuperAdmin } = useAuth();
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [customRoles, setCustomRoles] = useState<RoleOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const orgId = (location.state as any)?.orgId;

  const form = useForm<z.infer<typeof editUserSchema>>({
    resolver: zodResolver(editUserSchema),
    mode: 'onChange',
    defaultValues: {
      name: '',
      email: '',
      selectedRoleIds: ['user'],
      primaryRoleId: 'user',
      is_active: true,
      email_verified: false,
    },
  });

  useEffect(() => {
    const savedDarkMode = useUIStore.getState().darkMode;
    if (savedDarkMode) document.documentElement.classList.add('dark');
  }, []);

  // Fetch user data
  useEffect(() => {
    if (!isSuperAdmin()) {
      setError('Access Denied: Only super administrators can edit users.');
      return;
    }
    if (!id) {
      setError('User ID is required.');
      return;
    }

    const fetchUser = async () => {
      setLoading(true);
      setError(null);
      try {
        let user: User;
        if (orgId) {
          user = await api.get(`api/v1/organizations/${orgId}/users/${id}`).json() as User;
        } else {
          user = await api.get(`api/v1/users/${id}`).json() as User;
        }
        setCurrentUser(user);
      } catch (err: any) {
        setError(err.message || 'Failed to fetch user details');
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [id, orgId, isSuperAdmin]);

  // Fetch org roles
  useEffect(() => {
    if (!orgId) return;
    setLoadingRoles(true);
    getRoles(orgId)
      .then((res: any) => {
        const roles: Role[] = res.roles || [];
        setCustomRoles(roles.map(r => ({
          id: r.id,
          name: r.name,
          description: r.description,
          isSystem: false,
          permissions: r.permissions,
        })));
      })
      .catch(() => {})
      .finally(() => setLoadingRoles(false));
  }, [orgId]);

  // Populate form once both user and customRoles are ready
  useEffect(() => {
    if (!currentUser) return;

    const userSystemRole = currentUser.role === 'super_admin' ? 'admin' : currentUser.role;
    const userRoles: string[] = currentUser.roles || [];

    // Match custom roles by id or name against what the user has
    const matchedCustomIds = customRoles
      .filter(r => userRoles.includes(r.id) || userRoles.includes(r.name))
      .map(r => r.id);

    const allSelected = Array.from(new Set([userSystemRole, ...matchedCustomIds]));
    const primary = userSystemRole; // system role is always the starting primary

    form.reset({
      name: currentUser.name || '',
      email: currentUser.email || '',
      selectedRoleIds: allSelected,
      primaryRoleId: primary,
      is_active: currentUser.is_active ?? true,
      email_verified: currentUser.email_verified,
    });
  }, [currentUser, customRoles, form]);

  const onSubmit = async (data: z.infer<typeof editUserSchema>) => {
    if (!id) return;
    setIsSubmitting(true);
    setError(null);

    try {
      const systemRoleIds = SYSTEM_ROLES.map(r => r.id);
      const allRoles = [...SYSTEM_ROLES, ...customRoles];

      // `role` = primary role's value: system roles use their id ('user'/'admin'),
      // custom roles use their name
      const primaryRole = allRoles.find(r => r.id === data.primaryRoleId);
      const apiRole = primaryRole
        ? (primaryRole.isSystem ? primaryRole.id : primaryRole.name)
        : data.primaryRoleId;

      // Build `roles` array: primary role + selected custom roles
      const customRoleNames = data.selectedRoleIds
        .filter(rid => !systemRoleIds.includes(rid))
        .map(rid => customRoles.find(r => r.id === rid)?.name)
        .filter((n): n is string => Boolean(n));

      const allRoleNames = [apiRole, ...customRoleNames.filter(n => n !== apiRole)];

      if (orgId) {
        await updateOrgUser(orgId, id, {
          name: data.name,
          email: data.email,
          role: apiRole,
          roles: allRoleNames,
          is_active: data.is_active,
          email_verified: data.email_verified,
        });
      } else {
        await api.put(`api/v1/users/${id}`, {
          json: { name: data.name, email: data.email, role: apiRole },
        }).json();
      }

      const primaryName = [...SYSTEM_ROLES, ...customRoles].find(r => r.id === data.primaryRoleId)?.name ?? data.primaryRoleId;
      toast({
        title: 'User Updated',
        description: `${data.name || data.email || 'User'} updated with ${data.selectedRoleIds.length} role(s). Primary: ${primaryName}.`,
      });

      navigate('/dashboard/users', { state: orgId ? { orgId } : undefined });
    } catch (err: any) {
      setError(err.message || 'Failed to update user');
      toast({
        title: 'Update Failed',
        description: err.message || 'Failed to update user',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!id || !orgId) return;
    if (!confirmDelete) { setConfirmDelete(true); return; }

    setIsDeleting(true);
    try {
      await deleteOrgUser(orgId, id);
      toast({
        title: 'User Deleted',
        description: `${currentUser?.name || currentUser?.email || 'User'} has been removed from the organization.`,
      });
      navigate('/dashboard/users', { state: { orgId } });
    } catch (err: any) {
      toast({ title: 'Delete Failed', description: err.message || 'Failed to delete user', variant: 'destructive' });
      setConfirmDelete(false);
    } finally {
      setIsDeleting(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><LoadingSpinner size={48} /></div>;
  }

  if (!isSuperAdmin()) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="mb-6 flex items-center justify-between">
          <Link to="/dashboard/users">
            <Button variant="ghost" className="mb-4"><ArrowLeft className="h-4 w-4 mr-2" />Back to Users</Button>
          </Link>
          <ThemeToggleCompact />
        </div>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Access Denied</AlertTitle>
          <AlertDescription>Only super administrators can edit users.</AlertDescription>
        </Alert>
      </div>
    );
  }

  const selectedRoleIds = form.watch('selectedRoleIds');
  const primaryRoleId = form.watch('primaryRoleId');
  const allRoles = [...SYSTEM_ROLES, ...customRoles];

  const toggleRole = (roleId: string) => {
    const current = form.getValues('selectedRoleIds');
    const next = current.includes(roleId)
      ? current.filter(rid => rid !== roleId)
      : [...current, roleId];

    form.setValue('selectedRoleIds', next, { shouldValidate: true });

    if (!next.includes(form.getValues('primaryRoleId'))) {
      form.setValue('primaryRoleId', next[0] ?? '', { shouldValidate: true });
    }
  };

  const setPrimary = (roleId: string) => {
    form.setValue('primaryRoleId', roleId, { shouldValidate: true });
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-4">
        <Link to="/dashboard/users" state={orgId ? { orgId } : undefined}>
          <Button variant="ghost" className="mb-2">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Users
          </Button>
        </Link>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Logo" className="h-10 w-auto object-contain" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Edit User</h1>
              <p className="text-gray-600 dark:text-gray-400 text-sm mt-0.5">Update user account information</p>
            </div>
          </div>
          <ThemeToggleCompact />
        </div>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {currentUser && (
        <Card>
          <CardHeader>
            <CardTitle>User Details</CardTitle>
            <CardDescription>
              Update the account information for {currentUser.name || currentUser.email}
            </CardDescription>
          </CardHeader>

          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email *</FormLabel>
                      <FormControl>
                        <Input type="email" placeholder="user@example.com" {...field} />
                      </FormControl>
                      <FormDescription>The user's email address (used for login)</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Name (Optional)</FormLabel>
                      <FormControl>
                        <Input placeholder="John Doe" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Unified Role Selection */}
                <FormItem>
                  <FormLabel>Roles *</FormLabel>
                  <FormDescription>
                    Select one or more roles. Mark one as <span className="font-medium">Primary</span> — this determines the user's main account type.
                  </FormDescription>

                  <div className="border rounded-lg overflow-hidden mt-2">
                    {/* System roles */}
                    <div className="bg-gray-50 dark:bg-gray-900/50 px-3 py-1.5 border-b">
                      <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                        <Shield className="h-3 w-3" />
                        System Roles
                      </div>
                    </div>
                    {SYSTEM_ROLES.map((role, idx) => (
                      <RoleRow
                        key={role.id}
                        role={role}
                        isSelected={selectedRoleIds.includes(role.id)}
                        isPrimary={primaryRoleId === role.id}
                        isLast={idx === SYSTEM_ROLES.length - 1 && customRoles.length === 0}
                        onToggle={() => toggleRole(role.id)}
                        onSetPrimary={() => setPrimary(role.id)}
                        disabled={selectedRoleIds.length === 1 && selectedRoleIds.includes(role.id)}
                      />
                    ))}

                    {/* Custom roles */}
                    {loadingRoles ? (
                      <div className="px-3 py-4 text-sm text-gray-500 dark:text-gray-400 border-t">
                        Loading custom roles…
                      </div>
                    ) : customRoles.length > 0 ? (
                      <>
                        <div className="bg-gray-50 dark:bg-gray-900/50 px-3 py-1.5 border-t">
                          <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                            <Users className="h-3 w-3" />
                            Custom Roles
                          </div>
                        </div>
                        {customRoles.map((role, idx) => (
                          <RoleRow
                            key={role.id}
                            role={role}
                            isSelected={selectedRoleIds.includes(role.id)}
                            isPrimary={primaryRoleId === role.id}
                            isLast={idx === customRoles.length - 1}
                            onToggle={() => toggleRole(role.id)}
                            onSetPrimary={() => setPrimary(role.id)}
                            disabled={selectedRoleIds.length === 1 && selectedRoleIds.includes(role.id)}
                          />
                        ))}
                      </>
                    ) : (
                      <div className="px-3 py-3 text-sm text-gray-400 dark:text-gray-500 border-t italic">
                        No custom roles for this organization —{' '}
                        <Link to="/dashboard/roles" className="text-blue-600 dark:text-blue-400 underline not-italic">
                          create some
                        </Link>
                      </div>
                    )}
                  </div>

                  {form.formState.errors.selectedRoleIds && (
                    <p className="text-sm text-red-500 mt-1">{form.formState.errors.selectedRoleIds.message}</p>
                  )}
                  {form.formState.errors.primaryRoleId && (
                    <p className="text-sm text-red-500 mt-1">{form.formState.errors.primaryRoleId.message}</p>
                  )}

                  {/* Selected roles summary */}
                  {selectedRoleIds.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {selectedRoleIds.map(rid => {
                        const role = allRoles.find(r => r.id === rid);
                        return role ? (
                          <Badge key={rid} variant={rid === primaryRoleId ? 'default' : 'secondary'} className="gap-1">
                            {rid === primaryRoleId && <Star className="h-2.5 w-2.5 fill-current" />}
                            {role.name}
                          </Badge>
                        ) : null;
                      })}
                    </div>
                  )}
                </FormItem>

                {/* Account status (org context only) */}
                {orgId && (
                  <div className="space-y-3">
                    <FormField
                      control={form.control}
                      name="is_active"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-3">
                          <FormControl>
                            <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                          </FormControl>
                          <div className="space-y-1 leading-none">
                            <FormLabel>Account Active</FormLabel>
                            <FormDescription>Uncheck to deactivate this user account</FormDescription>
                          </div>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="email_verified"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-3">
                          <FormControl>
                            <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                          </FormControl>
                          <div className="space-y-1 leading-none">
                            <FormLabel>Email Verified</FormLabel>
                            <FormDescription>Mark the user's email as verified</FormDescription>
                          </div>
                        </FormItem>
                      )}
                    />
                  </div>
                )}

                {/* Current info */}
                <div className="border-t pt-4">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">Current Information</p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">User ID:</span>
                      <code className="ml-1 font-mono text-xs bg-gray-100 dark:bg-gray-800 dark:text-gray-200 px-1.5 py-0.5 rounded">{currentUser.id}</code>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Created:</span>
                      <span className="ml-1">{new Date(currentUser.created_at).toLocaleDateString()}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Email Verified:</span>
                      <span className="ml-1 font-medium">{currentUser.email_verified ? 'Yes' : 'No'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Locked:</span>
                      <span className="ml-1 font-medium">{currentUser.locked ? 'Yes' : 'No'}</span>
                    </div>
                    {currentUser.org_id && (
                      <div className="col-span-2">
                        <span className="text-gray-500 dark:text-gray-400">Organization ID:</span>
                        <code className="ml-1 font-mono text-xs bg-gray-100 dark:bg-gray-800 dark:text-gray-200 px-1.5 py-0.5 rounded">{currentUser.org_id}</code>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between pt-2">
                  <div className="flex gap-2">
                    <Button type="submit" disabled={isSubmitting}>
                      {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                      {isSubmitting ? 'Updating…' : 'Update User'}
                    </Button>
                    <Link to="/dashboard/users" state={orgId ? { orgId } : undefined}>
                      <Button variant="outline" type="button">Cancel</Button>
                    </Link>
                  </div>

                  {orgId && (
                    <div className="flex items-center gap-2">
                      {confirmDelete && (
                        <span className="text-sm text-red-600 dark:text-red-400">Are you sure?</span>
                      )}
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        disabled={isDeleting}
                        onClick={handleDelete}
                      >
                        {isDeleting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                        {confirmDelete ? 'Confirm Delete' : 'Delete User'}
                      </Button>
                      {confirmDelete && (
                        <Button type="button" variant="outline" size="sm" onClick={() => setConfirmDelete(false)}>
                          Cancel
                        </Button>
                      )}
                    </div>
                  )}
                </div>

              </form>
            </Form>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ── Role row component ──────────────────────────────────────────────────────

interface RoleRowProps {
  role: RoleOption;
  isSelected: boolean;
  isPrimary: boolean;
  isLast: boolean;
  disabled: boolean;
  onToggle: () => void;
  onSetPrimary: () => void;
}

function RoleRow({ role, isSelected, isPrimary, isLast, disabled, onToggle, onSetPrimary }: RoleRowProps) {
  return (
    <div
      className={[
        'flex items-start gap-3 px-3 py-3 transition-colors',
        !isLast && 'border-b',
        isSelected ? 'bg-blue-50/50 dark:bg-blue-950/20' : 'hover:bg-gray-50 dark:hover:bg-gray-800/40',
      ].filter(Boolean).join(' ')}
    >
      <Checkbox
        id={`role-check-${role.id}`}
        checked={isSelected}
        onCheckedChange={onToggle}
        disabled={disabled}
        className="mt-0.5 shrink-0"
      />

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <label htmlFor={`role-check-${role.id}`} className="text-sm font-medium cursor-pointer leading-none">
            {role.name}
          </label>
          {role.isSystem ? (
            <Badge variant="outline" className="text-xs py-0 px-1.5 border-gray-300 text-gray-500">system</Badge>
          ) : (
            <Badge variant="outline" className="text-xs py-0 px-1.5 border-blue-300 text-blue-600 dark:text-blue-400">custom</Badge>
          )}
        </div>
        {role.description && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 leading-snug">{role.description}</p>
        )}
        {role.permissions.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1.5">
            {role.permissions.slice(0, 4).map(p => (
              <Badge key={p} variant="secondary" className="text-xs px-1.5 py-0 font-normal">{p}</Badge>
            ))}
            {role.permissions.length > 4 && (
              <Badge variant="secondary" className="text-xs px-1.5 py-0 font-normal">
                +{role.permissions.length - 4} more
              </Badge>
            )}
          </div>
        )}
      </div>

      <div className="shrink-0">
        {isSelected ? (
          isPrimary ? (
            <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded px-2 py-0.5">
              <Star className="h-3 w-3 fill-current" />
              Primary
            </span>
          ) : (
            <button
              type="button"
              onClick={onSetPrimary}
              className="text-xs text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 border border-dashed border-gray-300 dark:border-gray-600 hover:border-blue-400 rounded px-2 py-0.5 transition-colors"
            >
              Set Primary
            </button>
          )
        ) : (
          <span className="text-xs text-gray-300 dark:text-gray-600 px-2 py-0.5">—</span>
        )}
      </div>
    </div>
  );
}
